<template>
    <div class="early-payoff-page">
        <available-offers v-show="section === 'available-offers'"
            :billing-profile="billingProfile"
            :selected-offer="selectedOffer"
            :settings="$parent.settings"
            :account="account"
            :min-payment-date="minPaymentDate"
            :error="error"
            :offers="offers"
            @select="offerSelected"
            @accept="previewOrAccept">
            <div :class="`bg-gray-100 text-${$root.theme}-800 font-semibold`">
                <div class="p-5 mx-auto md:w-2/3 lg:w-1/2 xl:w-1/3 2xl:w-1/4">
                    <p class="text-lg">{{ account.first_name }}, we have additional early payoff options available to you below.</p>
                </div>
            </div>
        </available-offers>

        <payment-authorization
            v-if="section === 'payment-authorization'"
            :account-id="details.account_id"
            :consumer-portal-data="data"
            :transactions="paymentAuthorizationTransactions"
            :template="paymentAuthorizationAgreementTemplate"
            @accepted="accept"
            @canceled="previewDeclined"
        />
        <offer-summary v-else-if="section === 'summary'"
            :consumer-portal-data="data"
            :selected-offer="selectedOffer"
            :payment-date="paymentDate"
            :transactions="transactions"
            :settings="$parent.settings"
            :details="details"
            :account="account"
            :electronic-consent-template="electronicConsentTemplate"
            :electronic-consent-declined-template="electronicConsentDeclinedTemplate"
            :error="error" />
        <div v-if="false" class="border-t border-gray-200">
            <div class="p-5 mx-auto md:w-2/3 lg:w-1/2 xl:w-1/3 2xl:w-1/4">
                <a :href="`${data.consumerPortalBaseUrl}/logout`">
                    <button id="logout-button" type="button" class="w-full justify-center text-black items-center inline-flex items-center px-3 text-lg pt-4 pb-3 border border-transparent border-gray-300 shadow-sm leading-4 font-medium rounded-md bg-gray-50 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-200">
                        <svg class="-ml-0.5 mr-3 h-4 w-4 mb-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        Logout
                    </button>
                </a>
            </div>
        </div>
    </div>
</template>

<script>
import Mixins from '../mixins';
import AvailableOffers from '../components/AvailableOffers';
import OfferSummary from '../components/OfferSummary';
import PaymentAuthorization from "../components/PaymentAuthorization.vue";

export default {
    mixins: [Mixins],

    components: {
        PaymentAuthorization,
        AvailableOffers,
        OfferSummary
    },

    props: [
        'data',
        'offer',
        'offers',
        'billing-profile',
        'account',
        'min-payment-date',
    ],

    created() {
        this.fbEvent('InitiateCheckout');

        this.$root.tiny = this.account.tiny;
    },

    mounted() {
        this.gtagEvent('early-payoff:loaded');
    },

    data() {
        return {
            pageForEvents: 'early-payoff-offers-page',
            section: 'available-offers',
            transactions: [],
            details: null,
            paymentMethod: null,
            selectedOffer: null,
            emailConfirmed: null,
            offerConfirmed: null,
            paymentDate: null,
            error: '',
            electronicConsentTemplate: null,
            electronicConsentDeclinedTemplate: null,
            paymentAuthorizationAgreementTemplate: null,
            paymentAuthorizationTransactions: null,
            forPreview: true,
            submittedPaymentMethod: null
        }
    },

    methods: {
        previewDeclined() {
            this.section = 'available-offers';

            this.$root.$refs.app.updatePageName('offers');
        },

        /**
         * @param {Object} selectedOffer
         * @returns {void}
         */
        offerSelected(selectedOffer) {
            this.selectedOffer = selectedOffer;

            this.gtagEvent(`selected-offer`);
            this.gtagEvent(`selected-offer:${this.selectedOffer.type}`);

            this.$http.post(`${this.data.consumerPortalBaseUrl}/api/tracking`, {
                event: 'last-action',
                value: 'selected-offer:' + this.selectedOffer.type
            }).then(() => {}, e => {});
        },

        previewOrAccept(submittedPaymentMethod) {
            this.forPreview = this.account.obtain_payment_authorization && this.$parent.settings.feature_payment_authorization_agreement;

            return this.accept(submittedPaymentMethod);
        },

        /**
         * @param {Object} submittedPaymentMethod
         * @returns {void}
         */
        accept(submittedPaymentMethod) {
            this.error = '';

            if (typeof submittedPaymentMethod === 'undefined') {
                submittedPaymentMethod = this.paymentMethod;
            } else {
                this.paymentMethod = submittedPaymentMethod;
            }

            // this is for low-payments-multi
            if (!submittedPaymentMethod.amount && typeof this.selectedOffer.amounts !== 'undefined' && this.selectedOffer.amounts.length) {
                submittedPaymentMethod.amount = this.selectedOffer.amounts[0];
            }

            if (this.selectedOffer.down_payment) submittedPaymentMethod.down_payment = this.selectedOffer.down_payment;

            if (!this.selectedOffer.type === 'custom' && submittedPaymentMethod.payment_type === 'full') {
                submittedPaymentMethod.amount = this.account.current_balance;
            }

            this.submitPayment({
                submittedPaymentMethod: {
                    ...submittedPaymentMethod,
                    ...{selected_offer: this.selectedOffer.name}
                },
                apiUrl: `${this.data.consumerPortalBaseUrl}/api/early-payoff-payments${this.forPreview ? '/preview' : ''}`,
                afterCallback: ({ data }) => {
                    this.transactions = data.transactions;
                    this.details = data.details;

                    if (this.forPreview) {
                        this.forPreview = false;

                        this.paymentAuthorizationAgreementTemplate = data.payment_authorization_agreement_template;
                        this.paymentAuthorizationTransactions = data.transactions;

                        this.section = 'payment-authorization';
                    } else {
                        this.electronicConsentTemplate = data.electronic_consent_template;
                        this.electronicConsentDeclinedTemplate = data.electronic_consent_declined_template;

                        this.section = 'summary';

                        this.gtagEvent(`payoff-accepted:${this.selectedOffer.name}`);

                        if (this.today === this.paymentDate) {
                            this.gtagEvent('offer-accepted:Early-Payoff');
                        } else {
                            this.gtagEvent('offer-accepted:Early-Payoff-Scheduled');
                        }
                    }
                },
                onError: error => {
                    if (error.hasOwnProperty('body')) {
                        if (error.body.hasOwnProperty('errors') && error.body.errors.hasOwnProperty('zip')) {
                            this.error = error.body.errors.zip[0];
                        } else if (error.body.hasOwnProperty('errors')) {
                            this.error = error.body.errors;
                        } else if (error.body.hasOwnProperty('error')) {
                            this.error = error.body.error;
                        }

                        this.section = 'available-offers';

                        setTimeout(() => {
                            let offerElement = document.getElementById('email-error');

                            if (offerElement) {
                                window.scrollTo({
                                    top: offerElement.offsetTop - 20,
                                    behavior: 'smooth'
                                });
                            }
                        }, 750);
                    } else {
                        throw error;
                    }
                }
            });
        },

        /**
         * @param {Boolean} wasConfirmed
         * @returns {void}
         */
        confirmEmail(wasConfirmed) {
            this.emailConfirmed = wasConfirmed;

            setTimeout(this.accept, 350);
        },

        /**
         * @param {Boolean} wasConfirmed
         * @returns {void}
         */
        confirmOffer(wasConfirmed) {
            this.offerConfirmed = wasConfirmed;

            if (this.offerConfirmed) {
                setTimeout(this.accept, 350);
            } else {
                this.offerConfirmed = null;
                this.emailConfirmed = null;
            }
        }
    }
}
</script>
